import { useEffect, useId, useState } from "react";
import { Field, type FieldInputProps } from "react-final-form";
import { useText } from "../../../hooks";
import type { LangKey } from "../../../types/lang";
import type { AnswerChoice, SimpleAnswer, SimpleQuestion } from "../../../types/question";
import { isRequired } from "../../../utils/validation";
import { Checkbox, Fieldset, FormControlLabel, FormHelperText, Legend } from "../../form-fields";
import { Stack } from "../../stack";
import { formatFieldName } from "../utils";

type Props = {
	question: SimpleQuestion;
};

const onCheckboxChange = (
	{ onChange, value = [] }: FieldInputProps<Array<SimpleAnswer>, HTMLElement>,
	checked: boolean,
	answerChoice: AnswerChoice<string>,
) => {
	const current: SimpleAnswer = {
		answerChoiceId: answerChoice.answerChoiceId,
		answerValue: answerChoice.value,
	};
	const values = checked
		? [current, ...value]
		: value.filter((v) => v.answerChoiceId !== answerChoice.answerChoiceId);
	onChange(values);
};

const getIsChecked = (
	{ value }: FieldInputProps<Array<SimpleAnswer>, HTMLElement>,
	answerChoice: AnswerChoice<string>,
) =>
	!!value &&
	value.some(
		(v) => v.answerChoiceId === answerChoice.answerChoiceId && v.answerValue === answerChoice.value,
	);

const MultipleChoiceQuestion = ({ question }: Props) => {
	const { questionId, question: questionText, answerChoices } = question;
	const describedById = useId();
	const [hasSubmitFailed, setHasSubmitError] = useState(false);

	const [t] = useText();

	return (
		<Fieldset
			aria-required
			aria-invalid={hasSubmitFailed}
			aria-describedby={hasSubmitFailed ? describedById : undefined}
		>
			<Legend>{questionText}</Legend>
			<Stack gap={2}>
				<Field<Array<SimpleAnswer>>
					name={formatFieldName(questionId)}
					format={(v) => v || []} // defaults the field to empty array instead of empty string
					validate={(values = []) => isRequired(values.map((v) => v.answerValue))} // todo: custom validation fn?
				>
					{({ input, meta }) => {
						// biome-ignore lint/correctness/useHookAtTopLevel: Not conditional
						useEffect(() => {
							setHasSubmitError(!!meta.submitFailed && !!meta.error);
						}, [meta]);
						return (
							<>
								{answerChoices.map((answerChoice) => {
									return (
										<FormControlLabel
											label={answerChoice.value}
											key={answerChoice.answerChoiceId}
											gap={2}
											data-testid={answerChoice.answerChoiceId}
										>
											<Checkbox
												checked={getIsChecked(input, answerChoice)}
												name={input.name}
												value={answerChoice.value}
												onChange={(event) => {
													onCheckboxChange(input, !!event.target.checked, answerChoice);
												}}
											/>
										</FormControlLabel>
									);
								})}
								{hasSubmitFailed && (
									<FormHelperText error={!!meta.error && hasSubmitFailed} id={describedById}>
										{t(meta.error as LangKey)}
									</FormHelperText>
								)}
							</>
						);
					}}
				</Field>
			</Stack>
		</Fieldset>
	);
};
export default MultipleChoiceQuestion;
